import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import './residents-page.css';
import NavigationV2 from '../../../components/v2/NavigationV2.jsx';
import ResidentTable, { COLUMN_DEFS } from './components/ResidentTable.jsx';
import usePersistedColumns from '../../../hooks/usePersistedColumns.js';
import usePersistedPerPage from '../../../hooks/usePersistedPerPage.js';
import ResidentDrawer from './components/ResidentDrawer.jsx';
import DeviceDrawer from './components/DeviceDrawer.jsx';
import GroupsTable, { GROUP_COLUMN_DEFS } from './components/GroupsTable.jsx';
import GroupDrawer from './components/GroupDrawer.jsx';
import ActiveFilterBar from './components/ActiveFilterBar.jsx';
import BulkActionBar from './components/BulkActionBar.jsx';
import ColumnVisibilityToggle from './components/ColumnVisibilityToggle.jsx';
import TableSkeleton from './components/TableSkeleton.jsx';
// V2 data layer — real API calls
import { getSession, getCampusId, getUserId, getSessionId, getOffsiteInfo, getCampusList, updateSessionCampus } from '../../../services/session.js';
import { fetchResidents, fetchAreas, fetchDevices, fetchGroupList, addResident, editResident, moveOutResidents, assignDevice, unassignDevice } from './api/residentsApi.js';
import { fetchGroups, fetchGroupCategories, addGroup, editGroup, deleteGroups } from './api/groupsApi.js';
import { exportResidentsCsv, exportGroupsCsv } from '../../../services/csvExport.js';

/** All column keys derived from COLUMN_DEFS */
const ALL_COLUMN_KEYS = COLUMN_DEFS.map((c) => c.key);

/** Default visible columns — show all by default (user can hide via column toggle) */
const DEFAULT_VISIBLE = ALL_COLUMN_KEYS;

/** All group column keys */
const ALL_GROUP_KEYS = GROUP_COLUMN_DEFS.map((c) => c.key);

/** Default visible columns for groups table — show all by default */
const DEFAULT_GROUP_VISIBLE = ALL_GROUP_KEYS;

/**
 * ResidentsPage — main orchestrator for the /community/residents V2 redesign.
 *
 * Manages: real API data loading, search, column filters, sort, pagination,
 * row selection, drawer state, and tab switching.
 *
 * Data layer: Uses session-based auth (encrypted dData from localStorage)
 * and calls the same backend endpoints as V1, via the V2 api client
 * (src/services/api.js) with domain-specific modules in ./api/.
 */
const ResidentsPage = ({ initialTab = 'residents' }) => {
  // ---------- Session ----------
  const sessionCampusId = getCampusId();
  const campusList = useMemo(() => getCampusList() || [], []);

  // ---------- Data & Loading ----------
  const [residents, setResidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [campusId, setCampusId] = useState(sessionCampusId || 0);

  // ---------- Lookup Data (from API) ----------
  const [areas, setAreas] = useState([]);
  const [groupList, setGroupList] = useState([]);
  const [availableDevices, setAvailableDevices] = useState([]);
  const [groupCategories, setGroupCategories] = useState([]);
  const firstLoad = useRef(true);

  // ---------- Tab ----------
  const activeTab = initialTab;

  // ---------- Search & Filters ----------
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [filters, setFilters] = useState({});

  // ---------- Sort ----------
  const [sortColumn, setSortColumn] = useState('resident_last_name');
  const [sortOrder, setSortOrder] = useState('asc');

  // ---------- Pagination ----------
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = usePersistedPerPage();

  // ---------- Column Visibility ----------
  const [visibleColumns, handleColumnToggle] = usePersistedColumns('residents', DEFAULT_VISIBLE, ALL_COLUMN_KEYS);

  // ---------- Row Selection ----------
  const [selectedIds, setSelectedIds] = useState([]);

  // ---------- Drawers ----------
  const [residentDrawerOpen, setResidentDrawerOpen] = useState(false);
  const [editingResident, setEditingResident] = useState(null);
  const [deviceDrawerOpen, setDeviceDrawerOpen] = useState(false);
  const [deviceResident, setDeviceResident] = useState(null);

  // ---------- Page-level Toast ----------
  const [toast, setToast] = useState(null); // { type: 'success' | 'error', message: string }
  const toastTimerRef = useRef(null);

  const showToast = useCallback((type, message) => {
    clearTimeout(toastTimerRef.current);
    setToast({ type, message });
    toastTimerRef.current = setTimeout(() => setToast(null), 4000);
  }, []);

  // Cleanup timer on unmount
  useEffect(() => () => clearTimeout(toastTimerRef.current), []);

  // ==========================================================================
  // GROUPS TAB STATE
  // ==========================================================================
  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(true);
  const [groupSearch, setGroupSearch] = useState('');
  const [groupFilters, setGroupFilters] = useState({});
  const [groupSortColumn, setGroupSortColumn] = useState('resident_group_name');
  const [groupSortOrder, setGroupSortOrder] = useState('asc');
  const [groupPage, setGroupPage] = useState(1);
  const [groupPerPage, setGroupPerPage] = usePersistedPerPage();
  const [groupVisibleColumns, setGroupVisibleColumns] = useState(DEFAULT_GROUP_VISIBLE);
  const [groupSelectedIds, setGroupSelectedIds] = useState([]);
  const [groupDrawerOpen, setGroupDrawerOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);

  // ---------- Helper: get session params for API write calls ----------
  const getSessionParams = useCallback(() => {
    const offsite = getOffsiteInfo();
    return {
      campusId,
      userId: getUserId(),
      clientId: getSessionId(),
      offsite: offsite.offsite,
      offsiteId: offsite.offsite_id,
    };
  }, [campusId]);

  // ---------- Load residents + lookup data from API ----------
  const loadResidents = useCallback(async () => {
    setLoading(true);
    try {
      const isFirstLoad = firstLoad.current;
      const [resData, areaData, deviceData, grpList] = await Promise.all([
        fetchResidents(campusId, !isFirstLoad),
        fetchAreas(campusId),
        fetchDevices(campusId),
        fetchGroupList(campusId),
      ]);
      firstLoad.current = false;
      setResidents(resData || []);
      setAreas(areaData || []);
      setAvailableDevices(deviceData || []);
      setGroupList(grpList || []);
    } catch (err) {
      console.error('[ResidentsPage] Failed to load residents data:', err);
    } finally {
      setLoading(false);
    }
  }, [campusId]);

  useEffect(() => { loadResidents(); }, [loadResidents]);

  // ---------- Load groups + categories from API ----------
  const loadGroups = useCallback(async () => {
    setGroupsLoading(true);
    try {
      const [grpData, catData] = await Promise.all([
        fetchGroups(campusId),
        fetchGroupCategories(),
      ]);
      setGroups(grpData || []);
      setGroupCategories(catData || []);
    } catch (err) {
      console.error('[ResidentsPage] Failed to load groups data:', err);
    } finally {
      setGroupsLoading(false);
    }
  }, [campusId]);

  useEffect(() => { loadGroups(); }, [loadGroups]);

  // ---------- Filter / Search / Sort pipeline ----------
  const filteredData = useMemo(() => {
    let data = [...residents];

    // 1. Active/inactive filter
    if (!showInactive) {
      data = data.filter((r) => r.is_active);
    }

    // 2. Search
    if (search.trim()) {
      const s = search.toLowerCase();
      data = data.filter(
        (r) =>
          r.resident_first_name?.toLowerCase().includes(s) ||
          r.resident_last_name?.toLowerCase().includes(s) ||
          r.resident_nickname?.toLowerCase().includes(s) ||
          r.area_name?.toLowerCase().includes(s) ||
          r.resident_phone?.includes(s) ||
          r.resident_email?.toLowerCase().includes(s) ||
          `${r.resident_first_name} ${r.resident_last_name}`.toLowerCase().includes(s)
      );
    }

    // 3. Column filters
    Object.entries(filters).forEach(([columnKey, values]) => {
      if (!values || values.length === 0) return;

      data = data.filter((row) => {
        if (columnKey === 'resident_groups') {
          const groups = row.resident_groups || [];
          if (values.includes('(none)') && groups.length === 0) return true;
          return groups.some((g) => values.includes(g.resident_group_name));
        }
        if (columnKey === 'status') {
          const status = row.status || (row.is_active ? 'Active' : 'Moved Out');
          return values.includes(status);
        }
        const val = row[columnKey];
        const strVal = val != null && val !== '' ? String(val) : '(empty)';
        return values.includes(strVal);
      });
    });

    // 4. Sort
    data.sort((a, b) => {
      let aVal = a[sortColumn];
      let bVal = b[sortColumn];

      if (sortColumn === 'status') {
        aVal = a.status || (a.is_active ? 'Active' : 'Moved Out');
        bVal = b.status || (b.is_active ? 'Active' : 'Moved Out');
      }

      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';

      const result = String(aVal).localeCompare(String(bVal), undefined, {
        numeric: true,
        sensitivity: 'base',
      });
      return sortOrder === 'asc' ? result : -result;
    });

    return data;
  }, [residents, showInactive, search, filters, sortColumn, sortOrder]);

  // ---------- Pagination ----------
  const totalFiltered = filteredData.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / perPage));
  const safePage = Math.min(page, totalPages);

  const paginatedData = useMemo(() => {
    const start = (safePage - 1) * perPage;
    return filteredData.slice(start, start + perPage);
  }, [filteredData, safePage, perPage]);

  const showingStart = totalFiltered === 0 ? 0 : (safePage - 1) * perPage + 1;
  const showingEnd = Math.min(safePage * perPage, totalFiltered);

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [search, filters, showInactive, perPage]);

  // ---------- Handlers ----------
  const handleSort = useCallback(
    (column) => {
      if (sortColumn === column) {
        setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortColumn(column);
        setSortOrder('asc');
      }
    },
    [sortColumn]
  );

  const handleFilterChange = useCallback((columnKey, values) => {
    setFilters((prev) => ({ ...prev, [columnKey]: values }));
  }, []);

  const handleRemoveFilter = useCallback((columnKey, value) => {
    setFilters((prev) => ({
      ...prev,
      [columnKey]: (prev[columnKey] || []).filter((v) => v !== value),
    }));
  }, []);

  const handleClearAllFilters = useCallback(() => {
    setFilters({});
    setSearch('');
  }, []);

  const handleSelectRow = useCallback((id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  }, []);

  const handleSelectAll = useCallback(() => {
    const allIds = paginatedData.map((r) => r.resident_id);
    const allSelected = allIds.every((id) => selectedIds.includes(id));
    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !allIds.includes(id)));
    } else {
      setSelectedIds((prev) => [...new Set([...prev, ...allIds])]);
    }
  }, [paginatedData, selectedIds]);

  // Drawer handlers
  const openAddResident = useCallback(() => {
    setEditingResident(null);
    setResidentDrawerOpen(true);
  }, []);

  const openEditResident = useCallback((resident) => {
    setEditingResident(resident);
    setResidentDrawerOpen(true);
  }, []);

  const openDeviceDrawer = useCallback((resident) => {
    setDeviceResident(resident);
    setDeviceDrawerOpen(true);
  }, []);

  const handleSaveResident = useCallback(
    async (formData) => {
      const session = getSessionParams();
      let result;

      // Map resident_groups objects to plain ID array for the API
      const groupIds = (formData.resident_groups || []).map((g) => g.resident_group_id);

      // Build API-ready data with correct field mappings
      const apiData = {
        ...formData,
        resident_group_ids: groupIds,
        ehr_external_id: formData.ehr_external_id || '',
      };

      if (formData.resident_id) {
        // Edit mode — find the original data for the OldPayload
        const original = residents.find((r) => r.resident_id === formData.resident_id);
        const oldGroupIds = (original?.resident_groups || []).map((g) => g.resident_group_id);
        const oldApiData = {
          ...original,
          resident_group_ids: oldGroupIds,
        };
        result = await editResident(apiData, oldApiData, session);
      } else {
        // Add mode
        result = await addResident(apiData, session);
      }

      if (result[0]) {
        setResidentDrawerOpen(false);
        loadResidents();
        showToast('success', formData.resident_id
          ? `${formData.resident_first_name} ${formData.resident_last_name} updated`
          : `${formData.resident_first_name} ${formData.resident_last_name} added`
        );
      } else {
        showToast('error', result[1] || 'Failed to save resident');
      }
    },
    [residents, getSessionParams, loadResidents, showToast]
  );

  const handleAssignDevice = useCallback(async (residentId, deviceId) => {
    const device = availableDevices.find((d) => d.device_id === deviceId);
    if (!device) return [false, 'Device not found'];

    const resident = residents.find((r) => r.resident_id === residentId);
    const residentAreaId = resident?.area_id || 0;

    const session = getSessionParams();
    const result = await assignDevice(device, residentId, session, residentAreaId);

    if (result[0]) {
      await loadResidents(); // Refresh data from backend
    }
    return result;
  }, [availableDevices, residents, getSessionParams, loadResidents]);

  const handleRemoveDevice = useCallback(async (residentId, deviceId) => {
    const session = getSessionParams();
    const result = await unassignDevice(deviceId, residentId, session);

    if (result[0]) {
      await loadResidents(); // Refresh data from backend
    }
    return result;
  }, [getSessionParams, loadResidents]);

  // Bulk action stubs
  const handleBulkExport = useCallback(() => {
    const selected = residents.filter((r) => selectedIds.includes(r.resident_id));
    if (selected.length) exportResidentsCsv(selected, 'residents-selected.csv', visibleColumns);
  }, [selectedIds, residents, visibleColumns]);

  // Page-level export: exports all currently-filtered residents
  const handleExportAll = useCallback(() => {
    if (filteredData.length) exportResidentsCsv(filteredData, 'residents.csv', visibleColumns);
  }, [filteredData, visibleColumns]);

  const handleBulkMoveOut = useCallback(async () => {
    if (!confirm(`Move out ${selectedIds.length} resident(s)? This will change their status to Moved Out.`)) return;
    const session = getSessionParams();
    const count = selectedIds.length;
    const result = await moveOutResidents(selectedIds, session);
    if (result[0]) {
      setSelectedIds([]);
      loadResidents();
      showToast('success', `${count} resident${count > 1 ? 's' : ''} moved out`);
    } else {
      showToast('error', result[1] || 'Move out failed');
    }
  }, [selectedIds, getSessionParams, loadResidents, showToast]);

  // Single-row move out
  const handleMoveOut = useCallback(async (resident) => {
    if (!confirm(`Move out ${resident.resident_first_name} ${resident.resident_last_name}?`)) return;
    const session = getSessionParams();
    const result = await moveOutResidents([resident.resident_id], session);
    if (result[0]) {
      loadResidents();
      showToast('success', `${resident.resident_first_name} ${resident.resident_last_name} moved out`);
    } else {
      showToast('error', result[1] || 'Move out failed');
    }
  }, [getSessionParams, loadResidents, showToast]);

  // ==========================================================================
  // GROUPS TAB — Enrich groups with members derived from residents data
  // ==========================================================================
  // The API returns groups with a flat `resident_name` string, not a `members` array.
  // We cross-reference the residents list (each resident has `resident_groups` array)
  // to build the `members` array that GroupResidentsCell and GroupDrawer expect.
  const enrichedGroups = useMemo(() => {
    if (!residents.length) return groups;
    return groups.map((group) => ({
      ...group,
      members: residents
        .filter((r) =>
          r.is_active &&
          (r.resident_groups || []).some((g) => g.resident_group_id === group.resident_group_id)
        )
        .map((r) => ({
          resident_id: r.resident_id,
          resident_name: `${r.resident_first_name} ${r.resident_last_name}`,
          area_name: r.area_name,
          area_type_name: r.area_type_name,
        })),
    }));
  }, [groups, residents]);

  // ==========================================================================
  // GROUPS TAB — Filter / Search / Sort pipeline
  // ==========================================================================
  const filteredGroups = useMemo(() => {
    let data = [...enrichedGroups];

    // 1. Search
    if (groupSearch.trim()) {
      const s = groupSearch.toLowerCase();
      data = data.filter(
        (g) =>
          g.resident_group_name?.toLowerCase().includes(s) ||
          g.resident_description?.toLowerCase().includes(s) ||
          g.resident_group_category_name?.toLowerCase().includes(s) ||
          (g.members || []).some((m) => m.resident_name?.toLowerCase().includes(s))
      );
    }

    // 2. Column filters
    Object.entries(groupFilters).forEach(([columnKey, values]) => {
      if (!values || values.length === 0) return;
      data = data.filter((row) => {
        const val = row[columnKey];
        const strVal = val != null && val !== '' ? String(val) : '(empty)';
        return values.includes(strVal);
      });
    });

    // 3. Sort
    data.sort((a, b) => {
      let aVal, bVal;
      if (groupSortColumn === 'resident_count') {
        aVal = (a.members || []).length;
        bVal = (b.members || []).length;
        const result = aVal - bVal;
        return groupSortOrder === 'asc' ? result : -result;
      }
      aVal = a[groupSortColumn];
      bVal = b[groupSortColumn];
      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';
      const result = String(aVal).localeCompare(String(bVal), undefined, {
        numeric: true,
        sensitivity: 'base',
      });
      return groupSortOrder === 'asc' ? result : -result;
    });

    return data;
  }, [enrichedGroups, groupSearch, groupFilters, groupSortColumn, groupSortOrder]);

  // Groups pagination
  const totalFilteredGroups = filteredGroups.length;
  const totalGroupPages = Math.max(1, Math.ceil(totalFilteredGroups / groupPerPage));
  const safeGroupPage = Math.min(groupPage, totalGroupPages);

  const paginatedGroups = useMemo(() => {
    const start = (safeGroupPage - 1) * groupPerPage;
    return filteredGroups.slice(start, start + groupPerPage);
  }, [filteredGroups, safeGroupPage, groupPerPage]);

  const groupShowingStart = totalFilteredGroups === 0 ? 0 : (safeGroupPage - 1) * groupPerPage + 1;
  const groupShowingEnd = Math.min(safeGroupPage * groupPerPage, totalFilteredGroups);

  // Reset group page when filters change
  useEffect(() => { setGroupPage(1); }, [groupSearch, groupFilters, groupPerPage]);

  // Groups page number buttons
  const groupPageNumbers = useMemo(() => {
    const pages = [];
    const maxButtons = 5;
    let start = Math.max(1, safeGroupPage - Math.floor(maxButtons / 2));
    let end = Math.min(totalGroupPages, start + maxButtons - 1);
    if (end - start < maxButtons - 1) {
      start = Math.max(1, end - maxButtons + 1);
    }
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  }, [safeGroupPage, totalGroupPages]);

  // Groups handlers
  const handleGroupSort = useCallback(
    (column) => {
      if (groupSortColumn === column) {
        setGroupSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
      } else {
        setGroupSortColumn(column);
        setGroupSortOrder('asc');
      }
    },
    [groupSortColumn]
  );

  const handleGroupFilterChange = useCallback((columnKey, values) => {
    setGroupFilters((prev) => ({ ...prev, [columnKey]: values }));
  }, []);

  const handleGroupRemoveFilter = useCallback((columnKey, value) => {
    setGroupFilters((prev) => ({
      ...prev,
      [columnKey]: (prev[columnKey] || []).filter((v) => v !== value),
    }));
  }, []);

  const handleGroupClearAllFilters = useCallback(() => {
    setGroupFilters({});
    setGroupSearch('');
  }, []);

  const handleGroupSelectRow = useCallback((id) => {
    setGroupSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  }, []);

  const handleGroupSelectAll = useCallback(() => {
    const allIds = paginatedGroups.map((g) => g.resident_group_id);
    const allSelected = allIds.every((id) => groupSelectedIds.includes(id));
    if (allSelected) {
      setGroupSelectedIds((prev) => prev.filter((id) => !allIds.includes(id)));
    } else {
      setGroupSelectedIds((prev) => [...new Set([...prev, ...allIds])]);
    }
  }, [paginatedGroups, groupSelectedIds]);

  const handleGroupColumnToggle = useCallback((columnKey) => {
    setGroupVisibleColumns((prev) =>
      prev.includes(columnKey) ? prev.filter((k) => k !== columnKey) : [...prev, columnKey]
    );
  }, []);

  // Group drawer handlers
  const openAddGroup = useCallback(() => {
    setEditingGroup(null);
    setGroupDrawerOpen(true);
  }, []);

  const openEditGroup = useCallback((group) => {
    setEditingGroup(group);
    setGroupDrawerOpen(true);
  }, []);

  const handleSaveGroup = useCallback(
    async (formData) => {
      const session = getSessionParams();
      let result;

      // Build resident_ids array from the members list for the API payload
      const residentIds = (formData.members || []).map((m) => m.resident_id);
      const apiData = { ...formData, resident_ids: residentIds };

      if (formData.resident_group_id) {
        // Edit mode — find original from enriched data for OldPayload
        const original = enrichedGroups.find((g) => g.resident_group_id === formData.resident_group_id);
        const oldResidentIds = (original?.members || []).map((m) => m.resident_id);
        const oldApiData = { ...original, resident_ids: oldResidentIds };
        result = await editGroup(apiData, oldApiData, session);
      } else {
        // Add mode
        result = await addGroup(apiData, session);
      }

      if (result[0]) {
        setGroupDrawerOpen(false);
        loadGroups();
        loadResidents();
        showToast('success', formData.resident_group_id
          ? `${formData.resident_group_name} updated`
          : `${formData.resident_group_name} created`
        );
      } else {
        showToast('error', result[1] || 'Failed to save group');
      }
    },
    [enrichedGroups, getSessionParams, loadGroups, loadResidents, showToast]
  );

  const handleDeleteGroup = useCallback(
    async (group) => {
      const session = getSessionParams();
      const groupName = group.resident_group_name;
      const result = await deleteGroups([group.resident_group_id], session);
      if (result[0]) {
        setGroupSelectedIds((prev) => prev.filter((id) => id !== group.resident_group_id));
        setGroupDrawerOpen(false);
        loadGroups();
        showToast('success', `${groupName} deleted`);
      } else {
        showToast('error', result[1] || 'Delete failed');
      }
    },
    [getSessionParams, loadGroups, showToast]
  );

  // Group bulk actions
  const handleGroupBulkExport = useCallback(() => {
    const selected = enrichedGroups.filter((g) => groupSelectedIds.includes(g.resident_group_id));
    if (selected.length) exportGroupsCsv(selected, 'resident-groups-selected.csv');
  }, [groupSelectedIds, enrichedGroups]);

  // Page-level export: exports all currently-filtered groups
  const handleGroupExportAll = useCallback(() => {
    if (filteredGroups.length) exportGroupsCsv(filteredGroups, 'resident-groups.csv');
  }, [filteredGroups]);

  const handleGroupBulkDelete = useCallback(async () => {
    if (!confirm(`Delete ${groupSelectedIds.length} group(s)? This cannot be undone.`)) return;
    const session = getSessionParams();
    const count = groupSelectedIds.length;
    const result = await deleteGroups(groupSelectedIds, session);
    if (result[0]) {
      setGroupSelectedIds([]);
      loadGroups();
      showToast('success', `${count} group${count > 1 ? 's' : ''} deleted`);
    } else {
      showToast('error', result[1] || 'Bulk delete failed');
    }
  }, [groupSelectedIds, getSessionParams, loadGroups, showToast]);

  // Group column label map for filter bar
  const groupColumnLabels = useMemo(
    () => Object.fromEntries(GROUP_COLUMN_DEFS.map((c) => [c.key, c.label])),
    []
  );

  // Column label map for filter bar
  const columnLabels = useMemo(
    () => Object.fromEntries(COLUMN_DEFS.map((c) => [c.key, c.label])),
    []
  );

  // The currently-selected campus name — derive from campusList (which correctly maps session.campusData names)
  const campusName = useMemo(() => {
    const match = campusList.find((c) => c.campus_id === campusId);
    return match?.campus_name || '';
  }, [campusId, campusList]);

  // Keep deviceResident in sync when residents array refreshes (e.g. after assign/remove)
  useEffect(() => {
    if (deviceResident && deviceDrawerOpen) {
      const updated = residents.find((r) => r.resident_id === deviceResident.resident_id);
      if (updated && updated !== deviceResident) {
        setDeviceResident(updated);
      }
    }
  }, [residents, deviceResident, deviceDrawerOpen]);

  // Device drawer data
  const deviceDrawerAssigned = deviceResident?.devices || [];

  // Page number buttons
  const pageNumbers = useMemo(() => {
    const pages = [];
    const maxButtons = 5;
    let start = Math.max(1, safePage - Math.floor(maxButtons / 2));
    let end = Math.min(totalPages, start + maxButtons - 1);
    if (end - start < maxButtons - 1) {
      start = Math.max(1, end - maxButtons + 1);
    }
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  }, [safePage, totalPages]);

  // Active resident count for tab badge
  const activeCount = residents.filter((r) => r.is_active).length;

  return (
    <>
    <NavigationV2 />
    <div className="nav2-main">
    <div className="rp-page">
      {/* Page-level toast notification */}
      {toast ? (
        <div
          className={`rp-toast ${toast.type === 'success' ? 'rp-toast--success' : 'rp-toast--error'}`}
          role="status"
          aria-live="polite"
        >
          {toast.type === 'success' ? (
            <svg className="rp-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ) : (
            <svg className="rp-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
            </svg>
          )}
          <span>{toast.message}</span>
          <button
            type="button"
            onClick={() => { clearTimeout(toastTimerRef.current); setToast(null); }}
            className="rp-toast__close"
            aria-label="Dismiss"
          >
            <svg className="rp-icon-xs" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      ) : <></>}

      {/* ============================================================
          PAGE HEADER AREA
          ============================================================ */}
      <div className="rp-page-header">
        <div className="rp-page-header__inner">
          {/* Breadcrumb + Campus Selector */}
          <div className="rp-breadcrumb-row">
            <div className="rp-breadcrumb">
              <span className="rp-breadcrumb__text">Community</span>
              <svg className="rp-icon-md rp-breadcrumb__separator" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
              </svg>
              <span className="rp-breadcrumb__current">Residents</span>
            </div>

            {/* Campus selector — prominent position */}
            <div className="rp-campus-row">
              <label htmlFor="campus-selector" className="rp-campus-label">
                Campus:
              </label>
              {campusList.length > 1 ? (
                <select
                  id="campus-selector"
                  value={campusId}
                  onChange={(e) => {
                    const val = parseInt(e.target.value, 10);
                    setCampusId(val);
                    updateSessionCampus(val);
                  }}
                  className="rp-campus-select"
                >
                  {campusList.map((c) => (
                    <option key={c.campus_id || c.CampusId} value={c.campus_id || c.CampusId}>
                      {c.campus_name || c.CampusName}
                    </option>
                  ))}
                </select>
              ) : (
                <span className="rp-campus-name">{campusName}</span>
              )}
            </div>
          </div>

          {/* Page title */}
          <h1 className="rp-page-title">Residents</h1>
        </div>
      </div>

      {/* ============================================================
          TAB BAR
          ============================================================ */}
      <div className="rp-tab-bar">
        <div className="rp-tab-bar__inner">
          <nav className="rp-tab-nav" aria-label="Tabs">
            <Link to="/community/residents" className={`rp-tab ${activeTab === 'residents' ? 'is-active' : ''}`}>
              Residents ({activeCount})
              {activeTab === 'residents' && (
                <span className="rp-tab__indicator" />
              )}
            </Link>
            <Link to="/community/residents/groups" className={`rp-tab ${activeTab === 'groups' ? 'is-active' : ''}`}>
              Resident Groups ({enrichedGroups.length})
              {activeTab === 'groups' && (
                <span className="rp-tab__indicator" />
              )}
            </Link>
          </nav>
        </div>
      </div>

      {/* ============================================================
          MAIN CONTENT
          ============================================================ */}
      <div className="rp-content">
        {activeTab === 'residents' ? (
          <>
            {/* ---------- Controls Row ---------- */}
            <div className="rp-controls">
              {/* Left: Search + Active toggle */}
              <div className="rp-controls__left">
                {/* Inline search */}
                <div className="rp-search">
                  <svg className="rp-icon-md rp-search__icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                  </svg>
                  <input
                    type="search"
                    placeholder="Search residents..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="rp-search__input"
                    aria-label="Search residents"
                  />
                </div>

                {/* Active/All toggle */}
                <div className="rp-toggle-group">
                  <button
                    type="button"
                    onClick={() => setShowInactive(false)}
                    className={`rp-toggle-btn ${!showInactive ? 'is-active' : ''}`}
                  >
                    Active Only ({activeCount})
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowInactive(true)}
                    className={`rp-toggle-btn ${showInactive ? 'is-active' : ''}`}
                  >
                    All ({residents.length})
                  </button>
                </div>
              </div>

              {/* Right: Columns + Export + Add */}
              <div className="rp-controls__right">
                <ColumnVisibilityToggle
                  columns={COLUMN_DEFS}
                  visibleColumns={visibleColumns}
                  onToggle={handleColumnToggle}
                />

                <button
                  type="button"
                  className="rp-btn rp-btn--secondary"
                  onClick={handleExportAll}
                  aria-label="Export residents"
                >
                  <svg className="rp-icon-md" style={{ color: 'var(--color-text-secondary)' }} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                  </svg>
                  Export
                </button>

                <button
                  type="button"
                  onClick={openAddResident}
                  className="rp-btn rp-btn--primary"
                  aria-label="Add new resident"
                >
                  <svg className="rp-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                  </svg>
                  New Resident
                </button>
              </div>
            </div>

            {/* ---------- Active Filter Bar ---------- */}
            <ActiveFilterBar
              filters={filters}
              columnLabels={columnLabels}
              onRemoveFilter={handleRemoveFilter}
              onClearAll={handleClearAllFilters}
            />

            {/* ---------- Bulk Action Bar ---------- */}
            <BulkActionBar
              selectedCount={selectedIds.length}
              onExport={handleBulkExport}
              onMoveOut={handleBulkMoveOut}
              onClearSelection={() => setSelectedIds([])}
            />

            {/* ---------- Table or Skeleton ---------- */}
            {loading ? (
              <TableSkeleton />
            ) : (
              <ResidentTable
                data={paginatedData}
                allData={residents}
                visibleColumns={visibleColumns}
                sortColumn={sortColumn}
                sortOrder={sortOrder}
                onSort={handleSort}
                filters={filters}
                onFilterChange={handleFilterChange}
                selectedIds={selectedIds}
                onSelectRow={handleSelectRow}
                onSelectAll={handleSelectAll}
                onEditResident={openEditResident}
                onManageDevices={openDeviceDrawer}
                onMoveOut={handleMoveOut}
                onAddResident={openAddResident}
                onClearFilters={handleClearAllFilters}
              />
            )}

            {/* ---------- Pagination ---------- */}
            {!loading && totalFiltered > 0 && (
              <div className="rp-pagination">
                <p className="rp-pagination__info">
                  Showing <span className="rp-pagination__emphasis">{showingStart}</span>–<span className="rp-pagination__emphasis">{showingEnd}</span> of{' '}
                  <span className="rp-pagination__emphasis">{totalFiltered}</span> residents
                </p>

                <div className="rp-pagination__controls">
                  {/* Rows per page */}
                  <div className="rp-per-page">
                    <label htmlFor="per-page" className="rp-per-page__label">Rows per page:</label>
                    <select
                      id="per-page"
                      value={perPage}
                      onChange={(e) => setPerPage(parseInt(e.target.value, 10))}
                      className="rp-per-page__select"
                    >
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                      <option value={200}>200</option>
                      <option value={300}>300</option>
                    </select>
                  </div>

                  {/* Page buttons */}
                  <nav className="rp-page-nav" aria-label="Pagination">
                    <button
                      type="button"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={safePage <= 1}
                      className="rp-page-btn"
                      aria-label="Previous page"
                    >
                      <svg className="rp-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                      </svg>
                    </button>

                    {pageNumbers.map((num) => (
                      <button
                        key={num}
                        type="button"
                        onClick={() => setPage(num)}
                        className={`rp-page-num ${num === safePage ? 'is-active' : ''}`}
                        aria-label={`Page ${num}`}
                        aria-current={num === safePage ? 'page' : undefined}
                      >
                        {num}
                      </button>
                    ))}

                    <button
                      type="button"
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={safePage >= totalPages}
                      className="rp-page-btn"
                      aria-label="Next page"
                    >
                      <svg className="rp-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                      </svg>
                    </button>
                  </nav>
                </div>
              </div>
            )}
          </>
        ) : (
          /* ============================================================
              GROUPS TAB
              ============================================================ */
          <>
            {/* ---------- Groups Controls Row ---------- */}
            <div className="rp-controls">
              <div className="rp-controls__left">
                <div className="rp-search">
                  <svg className="rp-icon-md rp-search__icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                  </svg>
                  <input
                    type="search"
                    placeholder="Search groups..."
                    value={groupSearch}
                    onChange={(e) => setGroupSearch(e.target.value)}
                    className="rp-search__input"
                    aria-label="Search groups"
                  />
                </div>
              </div>

              <div className="rp-controls__right">
                <ColumnVisibilityToggle
                  columns={GROUP_COLUMN_DEFS}
                  visibleColumns={groupVisibleColumns}
                  onToggle={handleGroupColumnToggle}
                />

                <button
                  type="button"
                  className="rp-btn rp-btn--secondary"
                  onClick={handleGroupExportAll}
                  aria-label="Export groups"
                >
                  <svg className="rp-icon-md" style={{ color: 'var(--color-text-secondary)' }} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                  </svg>
                  Export
                </button>

                <button
                  type="button"
                  onClick={openAddGroup}
                  className="rp-btn rp-btn--primary"
                  aria-label="Add new group"
                >
                  <svg className="rp-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                  </svg>
                  New Group
                </button>
              </div>
            </div>

            {/* ---------- Groups Active Filter Bar ---------- */}
            <ActiveFilterBar
              filters={groupFilters}
              columnLabels={groupColumnLabels}
              onRemoveFilter={handleGroupRemoveFilter}
              onClearAll={handleGroupClearAllFilters}
            />

            {/* ---------- Groups Bulk Action Bar ---------- */}
            {groupSelectedIds.length > 0 && (
              <div className="rp-bulk-bar">
                <div className="rp-bulk-bar__left">
                  <span className="rp-bulk-bar__count">
                    {groupSelectedIds.length} group{groupSelectedIds.length !== 1 ? 's' : ''} selected
                  </span>
                  <button
                    type="button"
                    onClick={() => setGroupSelectedIds([])}
                    className="rp-bulk-bar__deselect"
                  >
                    Deselect All
                  </button>
                </div>
                <div className="rp-bulk-bar__right">
                  <button
                    type="button"
                    onClick={handleGroupBulkExport}
                    className="rp-bulk-btn"
                    aria-label="Export selected groups"
                  >
                    <svg className="rp-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                    </svg>
                    Export
                  </button>
                  <button
                    type="button"
                    onClick={handleGroupBulkDelete}
                    className="rp-bulk-btn rp-bulk-btn--danger"
                    aria-label="Delete selected groups"
                  >
                    <svg className="rp-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                    </svg>
                    Delete
                  </button>
                </div>
              </div>
            )}

            {/* ---------- Groups Table or Skeleton ---------- */}
            {groupsLoading ? (
              <TableSkeleton />
            ) : (
              <GroupsTable
                data={paginatedGroups}
                allData={enrichedGroups}
                visibleColumns={groupVisibleColumns}
                sortColumn={groupSortColumn}
                sortOrder={groupSortOrder}
                onSort={handleGroupSort}
                filters={groupFilters}
                onFilterChange={handleGroupFilterChange}
                selectedIds={groupSelectedIds}
                onSelectRow={handleGroupSelectRow}
                onSelectAll={handleGroupSelectAll}
                onEditGroup={openEditGroup}
                onDeleteGroup={handleDeleteGroup}
                onAddGroup={openAddGroup}
                onClearFilters={handleGroupClearAllFilters}
              />
            )}

            {/* ---------- Groups Pagination ---------- */}
            {!groupsLoading && totalFilteredGroups > 0 && (
              <div className="rp-pagination">
                <p className="rp-pagination__info">
                  Showing <span className="rp-pagination__emphasis">{groupShowingStart}</span>–<span className="rp-pagination__emphasis">{groupShowingEnd}</span> of{' '}
                  <span className="rp-pagination__emphasis">{totalFilteredGroups}</span> groups
                </p>

                <div className="rp-pagination__controls">
                  <div className="rp-per-page">
                    <label htmlFor="group-per-page" className="rp-per-page__label">Rows per page:</label>
                    <select
                      id="group-per-page"
                      value={groupPerPage}
                      onChange={(e) => setGroupPerPage(parseInt(e.target.value, 10))}
                      className="rp-per-page__select"
                    >
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                      <option value={200}>200</option>
                      <option value={300}>300</option>
                    </select>
                  </div>

                  <nav className="rp-page-nav" aria-label="Groups pagination">
                    <button
                      type="button"
                      onClick={() => setGroupPage((p) => Math.max(1, p - 1))}
                      disabled={safeGroupPage <= 1}
                      className="rp-page-btn"
                      aria-label="Previous page"
                    >
                      <svg className="rp-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                      </svg>
                    </button>

                    {groupPageNumbers.map((num) => (
                      <button
                        key={num}
                        type="button"
                        onClick={() => setGroupPage(num)}
                        className={`rp-page-num ${num === safeGroupPage ? 'is-active' : ''}`}
                        aria-label={`Page ${num}`}
                        aria-current={num === safeGroupPage ? 'page' : undefined}
                      >
                        {num}
                      </button>
                    ))}

                    <button
                      type="button"
                      onClick={() => setGroupPage((p) => Math.min(totalGroupPages, p + 1))}
                      disabled={safeGroupPage >= totalGroupPages}
                      className="rp-page-btn"
                      aria-label="Next page"
                    >
                      <svg className="rp-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                      </svg>
                    </button>
                  </nav>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ============================================================
          DRAWERS
          ============================================================ */}
      <ResidentDrawer
        isOpen={residentDrawerOpen}
        onClose={() => setResidentDrawerOpen(false)}
        resident={editingResident}
        onSave={handleSaveResident}
        areas={areas}
        groups={groupList}
        offsiteActive={getOffsiteInfo().offsite}
        residents={residents}
      />

      <DeviceDrawer
        isOpen={deviceDrawerOpen}
        onClose={() => setDeviceDrawerOpen(false)}
        resident={deviceResident}
        assignedDevices={deviceDrawerAssigned}
        availableDevices={availableDevices}
        onAssignDevice={handleAssignDevice}
        onRemoveDevice={handleRemoveDevice}
      />

      <GroupDrawer
        isOpen={groupDrawerOpen}
        onClose={() => setGroupDrawerOpen(false)}
        group={editingGroup}
        onSave={handleSaveGroup}
        onDelete={handleDeleteGroup}
        categories={groupCategories}
        residents={residents}
      />
    </div>
    </div>
    </>
  );
};

export default ResidentsPage;
